import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ContentChild,
  TemplateRef,
  AfterViewInit,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { DataService, QueryColumn, QueryObjectsModel } from '@barthauer/abc/core/data';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { AbcContainerService, AbcMessageBoxComponent, MessageBoxModel } from '@barthauer/abc/ui-components';
import { DxGalleryComponent } from 'devextreme-angular';
import { Observable } from 'rxjs';

export interface GallerySelectionChange {
  index: number;
  item: any;
}

@Component({
    selector: 'abc-common-editors-gallery',
    templateUrl: './imageViewer.component.html',
    styleUrls: ['./imageViewer.component.scss'],
    standalone: false
})
export class ImageViewerComponent implements OnInit {
  @ViewChild('gallery') galleryRef: DxGalleryComponent;
  @ViewChild('messaegBoxContainer', { read: ViewContainerRef }) messaegBoxContainer: ViewContainerRef;
  @Input('useGallery') useGallery: boolean;
  @Input('enabled') enabled: boolean;
  @Input('messageBoxModel') messageBoxModel: MessageBoxModel;
  @Input('isDataForm') isDataForm: boolean;
  @Input('messageBoxImageIdentifier') messageBoxImageIdentifier: string;
  @Output() onItemClick = new EventEmitter();
  @Output() onSelectionChanged = new EventEmitter();
  @Output() onItemHold = new EventEmitter();
  @Output() onMessageBoxButtonClick = new EventEmitter();
  @Output() onUploadButtonClick = new EventEmitter();
  @Output() onDeleteButtonClick = new EventEmitter();
  @Output() onDialogButtonClick = new EventEmitter();
  @Output() selectedIndexChange = new EventEmitter<number>();
  @Output() selectionChange = new EventEmitter<GallerySelectionChange>();
  private settings: MessageSettings;
  private _selectedIndex = 0;
  public disableDelete: boolean = true;
  @Input('selectedIndex')
  get selectedIndex() {
    return this._selectedIndex;
  }
  set selectedIndex(v: number) {
    this._selectedIndex = v ?? 0;
    if (this.galleryRef) this.galleryRef.selectedIndex = this._selectedIndex;
  }
  private _source: any;
  @Input('dataSource')
  get source() {
    return this._source;
  }
  set source(data: any[]) {
    this._source = data ?? [];
    if (this.galleryRef) this.galleryRef.dataSource = this._source;
  }
  get selectedItem(): any {
    return (this._source && this._source[this._selectedIndex]) ?? null;
  }

  constructor(
    private dataService: DataService,
    private abcContainer: AbcContainerService,
    public iconRegistry: MatIconRegistry,
    public sanitizer: DomSanitizer,
    private messageBoxService: MessageBoxService
  ) {
    iconRegistry.addSvgIcon('replace_com', sanitizer.bypassSecurityTrustResourceUrl('assets/images/replaceImage.svg'));
    iconRegistry.addSvgIcon('delete_com', sanitizer.bypassSecurityTrustResourceUrl('assets/images/deleteImage.svg'));
  }

  public static createQueryColumns(omitObjectColumn?: boolean): Array<QueryColumn> {
    const queryColumns = new Array<QueryColumn>();
    queryColumns.push(
      {
        Name: 'Designation',
        OPath: 'Designation',
      },
      {
        Name: 'Id',
        OPath: 'Id',
      },
      {
        Name: 'ParentId',
        OPath: 'ParentId',
      },
      {
        Name: 'Date',
        OPath: 'Date',
      },
      {
        Name: 'InsertAt',
        OPath: 'GetDateTime(SysDateInsert, SysTimeInsert)',
      }
    );
    if (!omitObjectColumn) {
      queryColumns.push({
        Name: 'Object',
        OPath: 'Object',
        Format: 'Base64Image[200x200]',
      });
    }
    return queryColumns;
  }

  public createRequestDocuments(parentId: string, useFilenameInsteadOfObject?: boolean): QueryObjectsModel {
    return this.dataService.createSimpleQuery(
      '',
      'Document',
      `ParentId='${parentId}'`,
      ImageViewerComponent.createQueryColumns(useFilenameInsteadOfObject)
    );
  }

  public getDocumentImages(parentId): Observable<any> {
    const query = this.createRequestDocuments(parentId);
    return this.dataService.executeReadObjectsQuery(query);
  }

  contentReady(e) {
    this.galleryRef.selectedIndex = this.selectedIndex;
  }

  ngOnInit() {
    this.settings = new MessageSettings();
    this.settings.mode = MessageMode.Question;
    this.settings.icon = MessageBoxIcon.Question;
  }

  ngAfterViewInit() {
    this.galleryRef?.selectedIndexChange?.subscribe((i: number) => {
      this._selectedIndex = i;
      this.selectedIndexChange.emit(i);
    });
  }

  itemClick(e) {
    this.onItemClick.emit(e);
    if (e.itemData === this.messageBoxImageIdentifier && this.useGallery && this.enabled) {
      this.openMessageBox();
    }
  }

  itemHold(e) {
    this.onItemHold.emit(e);
  }

  selectionChanged(e) {
    this.onSelectionChanged.emit(e);
    if (e.addedItems[0] !== this.messageBoxImageIdentifier) {
      this.disableDelete = false;
    } else if (e.addedItems[0] === this.messageBoxImageIdentifier) {
      this.disableDelete = true;
    }
    this.selectionChange.emit({ index: this._selectedIndex, item: this.selectedItem });
  }

  uploadClick() {
    this.onUploadButtonClick.emit(this.galleryRef.selectedItem);
    this.openMessageBox();
  }

  deleteClick() {
    this.onDeleteButtonClick.emit(this.galleryRef.selectedItem);
    if (this.galleryRef.selectedItem !== this.messageBoxImageIdentifier) {
      this.settings.button = MessageBoxButtons.YesNo;
      this.settings.message = 'Soll das Bild wirklich gelöscht werden?';
      this.messageBoxService.showMessageBox(this.settings).subscribe((result: DialogResult) => {
        if (DialogResult.Yes === result['result']) {
          this.onDialogButtonClick.emit();
          this.messageBoxService.dialog.closeAll();
        } else {
          this.messageBoxService.dialog.closeAll();
        }
      });
    }
  }

  openMessageBox() {
    let messageBoxRef = this.abcContainer.createComponent(this.messaegBoxContainer, AbcMessageBoxComponent);
    let messageBoxInstance = <AbcMessageBoxComponent>messageBoxRef.componentRef.instance;
    messageBoxInstance.messageBox = this.messageBoxModel;
    messageBoxInstance.onButtonClick.subscribe((choice) => {
      this.onMessageBoxButtonClick.emit(choice);
    });
  }
}
